import React from 'react'
import { Layers } from 'lucide-react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import FunnelByStage from './FunnelByStage'
import type { DashboardPipelineFunnel } from '@/hooks/useDashboardCrm'
import { formatNumber, formatPercent } from '@/lib/dashboardFormat'
import { useCurrency } from '@/hooks/useCurrency'
import { useTranslation } from 'react-i18next'

interface FunnelAccordionProps {
  pipelines: DashboardPipelineFunnel[]
  loading?: boolean
}

const FunnelAccordion: React.FC<FunnelAccordionProps> = ({ pipelines, loading = false }) => {
  const { t } = useTranslation('reports')
  const { formatCurrency } = useCurrency()

  if (loading) {
    return <FunnelByStage stages={[]} loading />
  }

  if (pipelines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-[20px] border border-border bg-card p-10 text-center shadow-sm">
        <Layers className="h-6 w-6 text-muted-foreground" />
        <p className="text-[14px] font-medium text-foreground">{t('dashboard.funnelEmpty')}</p>
        <p className="max-w-[320px] text-[12px] text-muted-foreground">
          {t('dashboard.funnelEmptyDesc')}
        </p>
      </div>
    )
  }

  // With a single funnel there is nothing to disclose: collapsing it would only hide the
  // one thing this section exists to show
  if (pipelines.length === 1) {
    return <FunnelByStage stages={pipelines[0].stages} />
  }

  const totalOpenValue = pipelines.reduce((sum, pipeline) => sum + pipeline.open_value, 0)

  return (
    <Accordion
      type="multiple"
      defaultValue={[]}
      className="flex flex-col gap-3"
    >
      {pipelines.map((pipeline) => {
        const share = totalOpenValue > 0 ? (pipeline.open_value / totalOpenValue) * 100 : 0

        return (
          <AccordionItem
            key={pipeline.pipeline_id}
            value={pipeline.pipeline_id}
            // The base item drops its bottom border on the last child, which only suits a
            // stacked list; here each funnel is a separate card
            className="last:border-b"
          >
            <AccordionTrigger>
              <div className="flex flex-1 flex-wrap items-baseline justify-between gap-x-4 gap-y-1 pr-2">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[14px] font-semibold text-foreground">{pipeline.name}</span>
                  <span className="text-[12px] font-normal text-muted-foreground">
                    {t('dashboard.openLeads', { count: pipeline.open_count })} ·{' '}
                    {t('dashboard.totalSuffix', { count: pipeline.total_count })}
                  </span>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="text-[14px] font-semibold tabular-nums text-foreground">
                    {formatCurrency(pipeline.open_value)}
                  </span>
                  {totalOpenValue > 0 && (
                    <span className="text-[11px] font-normal text-muted-foreground">
                      {formatPercent(share)}
                    </span>
                  )}
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <FunnelByStage stages={pipeline.stages} bare />
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </Accordion>
  )
}

export default FunnelAccordion
